/**
 * Build-with-AI (S9) — turns an accepted proposal into a persisted tile.
 * Each add runs its OWN preview (read-only, S3 policy) to shape chart/stat/
 * pivot config from the REAL result columns (never guessed), then persists
 * via the repository (`useDashboardStore.addTile` — the sole writer). Owns
 * the per-proposal status map so `BuildWithAiPanel` stays presentational;
 * partial failures are per-proposal, never silently dropped.
 */
import { useCallback, useState } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useDashboardStore } from '../store/dashboardStore';
import type { Dashboard, DashboardEngineId, DashboardTile, TableTile } from '../types/dashboardTypes';
import { startTileExecution } from '../services/tileExecutionService';
import type { TileProposal } from '../services/aiTileProposal';
import {
  buildTileFromSuggestion,
  pickSuggestionForProposal,
  shapeSuggestionsForProposal,
} from '../services/aiProposalToTile';
import { DEFAULT_TILE_SIZE } from '../utils/tileDefaultSize';
import type { AiProposalStatus } from '../components/AiProposalCard';

const nextFreeY = (tiles: DashboardTile[]): number =>
  tiles.reduce((max, t) => Math.max(max, t.layout.y + t.layout.h), 0);

export type ProposalRuntime = { status: AiProposalStatus; error?: string };

export function useDashboardAiTileAdd(
  dashboard: Dashboard,
  connectionId: string,
  database: string | undefined,
  engine: DashboardEngineId | undefined,
) {
  const { track } = useAnalytics();
  const [statuses, setStatuses] = useState<Record<string, ProposalRuntime>>({});

  const seed = useCallback((proposals: TileProposal[]) => {
    setStatuses(Object.fromEntries(proposals.map((p) => [p.id, { status: 'pending' as const }])));
  }, []);

  const reject = useCallback((id: string) => {
    setStatuses((s) => ({ ...s, [id]: { status: 'rejected' } }));
  }, []);

  const addProposal = useCallback(
    async (proposal: TileProposal): Promise<'added' | 'failed'> => {
      setStatuses((s) => ({ ...s, [proposal.id]: { status: 'adding' } }));
      const previewTile: TableTile = {
        id: `ai-preview-${proposal.id}`,
        tileType: 'table',
        title: 'AI preview',
        layout: { x: 0, y: 0, w: 1, h: 1 },
        connectionId,
        database,
        source: { kind: 'sql', sql: proposal.sql },
        refresh: { mode: 'manual' },
        rowLimit: 1000,
        density: 'compact',
      };
      const { promise } = startTileExecution(previewTile, null, [], {
        trigger: 'preview',
        dashboardId: dashboard.id,
      });
      const result = await promise;
      if (!result.ok) {
        const message = result.blockedByPolicy ?? result.error ?? 'Could not run this query';
        setStatuses((s) => ({ ...s, [proposal.id]: { status: 'failed', error: message } }));
        return 'failed';
      }
      const picked = result.resultSets[0];
      const suggestions = shapeSuggestionsForProposal(picked?.columns ?? [], picked?.previewRows ?? []);
      const suggestion = pickSuggestionForProposal(suggestions, proposal.tileType);
      const fresh = useDashboardStore.getState().dashboards.find((d) => d.id === dashboard.id) ?? dashboard;
      const tile = buildTileFromSuggestion({
        proposal,
        suggestion,
        connectionId,
        database,
        engine,
        layout: { x: 0, y: nextFreeY(fresh.tiles), ...DEFAULT_TILE_SIZE[suggestion.tileType] },
      });
      const addResult = await useDashboardStore.getState().addTile(dashboard.id, tile);
      if (!addResult.ok) {
        setStatuses((s) => ({
          ...s,
          [proposal.id]: { status: 'failed', error: addResult.detail ?? 'Could not save the tile' },
        }));
        return 'failed';
      }
      setStatuses((s) => ({ ...s, [proposal.id]: { status: 'added' } }));
      return 'added';
    },
    [connectionId, database, engine, dashboard],
  );

  const addOne = useCallback(
    async (proposal: TileProposal) => {
      const outcome = await addProposal(proposal);
      if (outcome === 'added') track('feature:dashboard_ai_add', { count: 1 });
    },
    [addProposal, track],
  );

  const addAll = useCallback(
    async (proposals: TileProposal[]) => {
      let addedCount = 0;
      for (const p of proposals) {
        // eslint-disable-next-line no-await-in-loop -- sequential so layout Y positions don't collide
        const outcome = await addProposal(p);
        if (outcome === 'added') addedCount++;
      }
      if (addedCount > 0) track('feature:dashboard_ai_add', { count: addedCount });
    },
    [addProposal, track],
  );

  return { statuses, seed, reject, addOne, addAll };
}
